import { set, get } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { dasherize, underscore } from '@ember/string';
import { Promise as EmberPromise, all } from 'rsvp';

// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  splitio: service(),
  statsig: service(),

  isBooted: false,
  isStatsigBooted: false,
  isTimedOut: false,
  trafficType: 'company',

  init() {
    this._super(...arguments);
    this.features = [];
    const splitio = this.splitio;
    splitio.on(`${this.trafficType}_update`, this, 'notifyFeatures');
    splitio.on(`${this.trafficType}_timeout`, this, 'toggleIsTimedOut');

    const statsig = this.statsig;
    statsig.on('update', this, 'notifyStatsigGates');
    statsig.on('error', this, 'toggleIsTimedOut');
  },

  notifyFeatures() {
    set(this, 'isBooted', true);
    this._notifyPropertyChanges();
  },

  notifyStatsigGates() {
    set(this, 'isStatsigBooted', true);
    this._notifyPropertyChanges();
  },

  _notifyPropertyChanges() {
    this.features.forEach((feature) => {
      this.notifyPropertyChange(feature);
    });
  },

  toggleIsTimedOut() {
    set(this, 'isTimedOut', true);
  },

  isEnabled(key, ...rest) {
    const name = dasherize(key);
    const gateName = underscore(key);
    return this.splitio.isEnabled(this.trafficType, name, ...rest) || this.statsig.isEnabled(gateName, true);
  },

  // Used to check eligibility of user to see feature gate experience, does not log statsig exposure for pulse metrics
  isEnabledStatsigExposure(key, checkWithExposure = false) {
    const gateName = underscore(key);
    return this.statsig.isEnabled(gateName, checkWithExposure);
  },

  // Used to log exposure to a feature gate, marks user and company for tracking within pulse metrics
  logStatsigExposure(key) {
    const gateName = underscore(key);
    this.statsig.manuallyLogGateExposure(gateName);
  },

  unknownProperty(key) {
    const features = this.features;
    const name = dasherize(key);
    // We add the key to features so later on we know to notify property change
    // if split updates
    features.addObject(key);
    return this.isEnabled(name);
  },

  setKey(key) {
    this.splitio.setKey(key, this.trafficType);
  },

  setProperty(value) {
    this.splitio.setKey(get(value, 'id'), this.trafficType);
    this.statsig.setProperty(this.trafficType, value);
  },

  setProperties(props) {
    Object.keys(props).forEach((prop) => {
      this.splitio.setKey(get(props[prop], 'id'), prop);
    });
    this.statsig.setPropertiesBatch(props);
  },

  ready() {
    try {
      if (this.isBooted && this.isStatsigBooted) {
        return EmberPromise.resolve();
      } else if (this.isTimedOut) {
        return EmberPromise.reject();
      } else {
        const splitPromise = new EmberPromise((resolve, reject) => {
          this.splitio.on(`${this.trafficType}_update`, this, () => resolve());
          this.splitio.on(`${this.trafficType}_timeout`, this, reject);
        });

        const statsigPromise = new EmberPromise((resolve, reject) => {
          this.statsig.on('update', this, () => resolve());
          this.statsig.on('wait', this, () => resolve());
          this.statsig.on('error', this, reject);
        });

        return all([splitPromise, statsigPromise]);
      }
    } catch (error) {
      /* eslint-disable no-console */
      console.error({ error });
      /* eslint-enable no-console */
      return EmberPromise.reject();
    }
  },
});
